/**
 * AutoArrivalEditor — may the driver's phone mark arrival on its own when the truck reaches
 * the site (OPEN-220a / DEC-386, Corbin)?
 *
 * Reads / saves GET / PATCH /v1/tenants/settings (read VIEW_RUN_TICKETS, write EDIT_GEOFENCE:
 * dispatch manager and up, the same gate as the Geofences page's mode). Two switches, both OFF
 * by default: Pickup (a tank battery's geofence records "Arrived at pickup") and Disposal (a
 * drop-off geofence records "Arrived at disposal"). Each saves when it is ticked.
 *
 * Off: the phone still notes that the truck was there, but the driver taps Arrived. On: after
 * two minutes inside the site's geofence the phone records the arrival, tells the driver, and
 * the step shows "Automatic". Which sites have geofences is still the Geofences page's mode.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useRoleCode } from '../../lib/use-role-code.js';
import { GEOFENCE_MODE_ROLES } from '../../lib/role-gates.js';
import {
  useTenantSettings,
  useSaveTenantSettings,
  type SaveTenantSettingsBody,
} from '../geofences/use-tenant-settings.js';

const help: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

const SWITCHES: ReadonlyArray<{
  readonly key: 'autoArrivalPickup' | 'autoArrivalDisposal';
  readonly id: string;
  readonly label: string;
}> = [
  { key: 'autoArrivalPickup', id: 'auto-arrival-pickup', label: 'Pickup' },
  { key: 'autoArrivalDisposal', id: 'auto-arrival-disposal', label: 'Disposal' },
];

export function AutoArrivalEditor(): React.JSX.Element {
  const roleCode = useRoleCode();
  const canEdit = roleCode !== null && GEOFENCE_MODE_ROLES.has(roleCode);
  const { data, isLoading, isError, refetch } = useTenantSettings();
  const { mutate: saveSettings, isPending } = useSaveTenantSettings();

  if (isError) {
    return (
      <div style={{ color: 'var(--color-danger)' }}>
        <p style={{ marginBottom: '8px' }}>Could not load the arrival settings.</p>
        <button type="button" onClick={(): void => void refetch()}>
          Retry
        </button>
      </div>
    );
  }
  if (isLoading || data === undefined) {
    return (
      <p role="status" style={help}>
        Loading…
      </p>
    );
  }

  const save = (body: SaveTenantSettingsBody): void => {
    saveSettings(body, {
      onSuccess: () => toast.success('Arrival setting saved'),
      onError: () => toast.error('Save failed — please try again.'),
    });
  };

  return (
    <div style={{ maxWidth: '520px' }}>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontWeight: 600, marginBottom: '6px' }}>
          Mark arrival automatically when the truck reaches the site
        </legend>
        <div style={{ display: 'flex', gap: '20px' }}>
          {SWITCHES.map((s) => (
            <label
              key={s.key}
              htmlFor={s.id}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}
            >
              <input
                id={s.id}
                type="checkbox"
                checked={data.settings[s.key]}
                disabled={!canEdit || isPending}
                onChange={(e): void => save({ [s.key]: e.target.checked })}
              />
              {s.label}
            </label>
          ))}
        </div>
      </fieldset>
      {!canEdit && (
        <p style={{ ...help, marginTop: '6px' }}>Dispatch Manager role required to change.</p>
      )}
      <p style={{ ...help, marginTop: '10px' }}>
        When on, the driver&apos;s phone records &quot;Arrived&quot; after the truck has been inside
        the site&apos;s geofence for two minutes, tells the driver, and the step shows
        &quot;Automatic&quot; on the job. When off (the default), the driver taps Arrived. Which
        sites have geofences is set on the Geofences page.
      </p>
    </div>
  );
}
